import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

export default function LoadingSplash() {
  const [show, setShow] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setShow(false);
      document.body.style.overflow = "";
    }, 3400);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[120] grid place-items-center overflow-hidden"
          style={{ background: "#0a0a0a" }}
          data-testid="loading-splash"
        >
          {/* Rose + gold radial ambient */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(232,165,180,0.22) 0%, rgba(212,175,55,0.1) 28%, rgba(10,10,10,1) 68%)",
            }}
          />

          <div className="relative flex flex-col items-center z-10">
            {/* Two interlocking gold rings */}
            <div className="relative w-[280px] h-[160px] md:w-[360px] md:h-[200px]">
              {/* Glow behind the interlock */}
              <motion.div
                aria-hidden="true"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: [0, 0, 1, 0.7], scale: [0.5, 0.5, 1.2, 1] }}
                transition={{ duration: 3.2, times: [0, 0.55, 0.75, 1] }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  width: 200,
                  height: 200,
                  background:
                    "radial-gradient(circle, rgba(232,165,180,0.5) 0%, rgba(212,175,55,0.25) 45%, rgba(0,0,0,0) 75%)",
                  filter: "blur(20px)",
                }}
              />

              <svg
                viewBox="-180 -60 360 120"
                className="relative w-full h-full"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="ringGoldA" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#F7D26B" />
                    <stop offset="50%" stopColor="#D4AF37" />
                    <stop offset="100%" stopColor="#8B7322" />
                  </linearGradient>
                  <linearGradient id="ringRoseA" x1="1" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#F4BFC9" />
                    <stop offset="50%" stopColor="#E8A5B4" />
                    <stop offset="100%" stopColor="#B57685" />
                  </linearGradient>
                  <filter id="ringGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.2" result="b" />
                    <feMerge>
                      <feMergeNode in="b" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* LEFT RING — slides from far left, rotates into place */}
                <motion.g
                  initial={{ x: -130, opacity: 0, rotate: -25 }}
                  animate={{
                    x: [-130, -130, -30, -18],
                    opacity: [0, 1, 1, 1],
                    rotate: [-25, -25, -8, 0],
                  }}
                  transition={{
                    duration: 3.2,
                    times: [0, 0.25, 0.65, 0.78],
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{ transformBox: "fill-box", transformOrigin: "center" }}
                >
                  <motion.circle
                    cx="0"
                    cy="0"
                    r="38"
                    fill="none"
                    stroke="url(#ringGoldA)"
                    strokeWidth="3"
                    filter="url(#ringGlow)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: [0, 1] }}
                    transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                  />
                  {/* Sparkle diamond at top */}
                  <motion.path
                    d="M 0 -44 L 3 -38 L 0 -32 L -3 -38 Z"
                    fill="url(#ringGoldA)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: [0, 0, 1, 1], scale: [0, 0, 1.2, 1] }}
                    transition={{ duration: 3.2, times: [0, 0.5, 0.62, 0.78] }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  />
                </motion.g>

                {/* RIGHT RING — slides from far right */}
                <motion.g
                  initial={{ x: 130, opacity: 0, rotate: 25 }}
                  animate={{
                    x: [130, 130, 30, 18],
                    opacity: [0, 1, 1, 1],
                    rotate: [25, 25, 8, 0],
                  }}
                  transition={{
                    duration: 3.2,
                    times: [0, 0.25, 0.65, 0.78],
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{ transformBox: "fill-box", transformOrigin: "center" }}
                >
                  <motion.circle
                    cx="0"
                    cy="0"
                    r="38"
                    fill="none"
                    stroke="url(#ringRoseA)"
                    strokeWidth="3"
                    filter="url(#ringGlow)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: [0, 1] }}
                    transition={{ duration: 1.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                  />
                  <motion.path
                    d="M 0 -44 L 3 -38 L 0 -32 L -3 -38 Z"
                    fill="url(#ringRoseA)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: [0, 0, 1, 1], scale: [0, 0, 1.2, 1] }}
                    transition={{ duration: 3.2, times: [0, 0.5, 0.62, 0.78] }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  />
                </motion.g>

                {/* Sparkle particles around interlock */}
                {Array.from({ length: 10 }).map((_, i) => {
                  const angle = (i / 10) * Math.PI * 2;
                  const dx = Math.cos(angle) * 80;
                  const dy = Math.sin(angle) * 55;
                  return (
                    <motion.circle
                      key={i}
                      r={1.2}
                      fill={i % 2 === 0 ? "#D4AF37" : "#E8A5B4"}
                      initial={{ opacity: 0, cx: 0, cy: 0 }}
                      animate={{
                        opacity: [0, 0, 0, 1, 0],
                        cx: [0, 0, 0, dx, dx],
                        cy: [0, 0, 0, dy, dy],
                      }}
                      transition={{
                        duration: 3.2,
                        times: [0, 0.6, 0.72, 0.88, 1],
                        ease: "easeOut",
                      }}
                    />
                  );
                })}
              </svg>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 2.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-2 font-serif-display text-[var(--iw-ivory)] text-3xl md:text-5xl tracking-wide shimmer-text"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Invitation Wala
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 2.7 }}
              className="mt-2 overline text-[var(--iw-faint)]"
            >
              Two rings. One forever.
            </motion.div>

            <div className="mt-8 w-48 h-px bg-white/10 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 3.2, ease: "easeInOut" }}
                className="h-full"
                style={{
                  background:
                    "linear-gradient(to right, var(--iw-gold), var(--iw-rose))",
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
